import { ReactComponent as DownArrow } from 'assets/down-arrow.svg';
import { ReactComponent as ResearchIcon } from 'assets/icons/icon-book-closed.svg';
import { ReactComponent as SettingsIcon } from 'assets/icons/icon-cog.svg';
import { ReactComponent as ReportsIcon } from 'assets/icons/icon-document-notes.svg';
import { ReactComponent as SignOutIcon } from 'assets/icons/icon-door-exit.svg';
import {
  ReactComponent as BankIcon,
  ReactComponent as FactsheetsIcon,
} from 'assets/icons/icon-library.svg';
import { ReactComponent as MoneyIcon } from 'assets/icons/icon-money.svg';
import { ReactComponent as HomeIcon } from 'assets/icons/icon-monitor.svg';
import { ReactComponent as OfficeIcon } from 'assets/icons/icon-office.svg';
import { ReactComponent as UsersIcon } from 'assets/icons/icon-user-couple.svg';
import { ReactComponent as UserIcon } from 'assets/icons/icon-user.svg';
import { ReactComponent as WalletIcon } from 'assets/icons/icon-wallet.svg';
import AppMenuHeader from 'components/AppMenuHeader';
import Button from 'components/Button';
import MenuItem from 'components/MenuItem';
import Modal from 'components/Modal/Modal';
import { useHasura } from 'context/ApiState';
import React from 'react';
import { routeMap, useNavigate } from 'routeMap';
import styled from 'styled-components';
import Select from './Select';
import Auth from '@aws-amplify/auth';
import { useAuthWrapperContext } from 'toolbox/src/components/AuthWrapper/AuthWrapper';

const AppMenu = () => {
  const { app_user, organisations, state, updateAppUser } = useHasura();
  const { groups, refreshAuthState } = useAuthWrapperContext();
  const isAdmin = groups?.includes('admin') || false;

  const selected_organisation_id =
    app_user?.selected_organisation?.id ||
    app_user?.organisations[0]?.organisation?.id;

  const updateOrgHandler = async (id) => {
    updateAppUser({ selected_organisation_id: id }).then(() =>
      window.location.reload(),
    );
  };

  const selectHandler = async (event) => {
    updateOrgHandler(event.currentTarget.value);
  };

  const signOutHandler = async () => {
    try {
      await Auth.signOut();
      refreshAuthState();
    } catch (error) {
      console.warn(error);
    }
  };

  const mainMenuItems = [
    { to: routeMap.home(), label: 'Home', icon: HomeIcon },
    { to: routeMap.reports(), label: 'Reports', icon: ReportsIcon },
    { to: routeMap.research(), label: 'Research', icon: ResearchIcon },
    { to: routeMap.factsheets(), label: 'Fact Sheets', icon: FactsheetsIcon },
  ];

  const bottomMenuItems = [
    { to: routeMap.settings(), label: 'Settings', icon: SettingsIcon },
  ];

  const adminMenuItems = [
    { to: routeMap.viewUsers(), label: 'Users', icon: UsersIcon },
    { to: routeMap.viewOrgs(), label: 'Organisations', icon: OfficeIcon },
    { to: routeMap.viewBanks(), label: 'Banks', icon: BankIcon },
    {
      to: routeMap.adminViewBankAccounts(),
      label: 'Bank Accounts',
      icon: WalletIcon,
    },
    { to: routeMap.adminViewBalances(), label: 'Balances', icon: MoneyIcon },
    { to: routeMap.adminResearch(), label: 'Research', icon: ResearchIcon },
  ];

  return (
    <nav className="z-10 flex flex-col flex-shrink-0 text-gray-300 md:w-64 bg-jcap-600 white-scrollbar">
      <AppMenuHeader />

      <div
        className="flex-col justify-between flex-grow hidden overflow-x-hidden overflow-y-auto md:flex"
        id="scrollWrap"
      >
        <div>
          <main className="px-4 py-1 mx-4 my-4 rounded bg-jcap-700">
            <Modal
              title="Change Organisation"
              button={(show) => (
                <div onClick={show} className="cursor-pointer">
                  <h2 className="flex justify-between w-full text-sm font-semibold leading-loose">
                    <span className="pt-px truncate select-none">
                      {state?.loading
                        ? '...'
                        : app_user?.selected_organisation?.name ??
                          'No Organisation'}
                    </span>
                    <span className="flex-shrink-0 ">
                      <DownArrow className="inline w-6 h-6 -mt-px" />
                    </span>
                  </h2>
                  <h3 className="flex items-center pb-1 text-sm text-jcap-300">
                    <UserIcon className="flex-shrink-0 w-3 h-3 mr-1" />
                    <span className="truncate select-none">{app_user?.id}</span>
                  </h3>
                </div>
              )}
              content={() => (
                <Select
                  label="Organisation"
                  value={selected_organisation_id}
                  onChange={selectHandler}
                >
                  {organisations?.length > 0 &&
                    organisations?.map((organisation, i) => (
                      <option key={i} value={organisation?.id}>
                        {organisation?.name}
                      </option>
                    ))}
                </Select>
              )}
              footer={(hide) => <Button onClick={hide}>Close</Button>}
            />
          </main>

          <ul>
            {mainMenuItems.map(({ to, label, icon }, i) => (
              <MenuItem key={i} to={to} label={label} Icon={icon} />
            ))}
          </ul>
        </div>

        <div className="pt-2">
          <ul className="pb-2">
            {bottomMenuItems.map(({ to, label, icon }, i) => (
              <MenuItem key={i} to={to} label={label} Icon={icon} />
            ))}
            <div className="-mt-2">
              <Modal
                button={(show) => (
                  <MenuItem
                    label={'Sign Out'}
                    Icon={SignOutIcon}
                    textClass="text-jcap-200"
                    onClick={show}
                  />
                )}
                title="Confirmation"
                content={() => <p>Are you sure you want to sign out?</p>}
                footer={(hide) => (
                  <>
                    <Button className="mr-2 btn btn-white" onClick={hide}>
                      Cancel
                    </Button>
                    <Button
                      className="btn btn-white-danger"
                      onClick={signOutHandler}
                    >
                      Yes, Sign Out
                    </Button>
                  </>
                )}
              />
            </div>
          </ul>

          {isAdmin && (
            <div className="pt-4 pb-4 m-4 mb-1 -mt-1 bg-gray-700 rounded-lg">
              <h3 className="pl-5 text-xs font-semibold tracking-wide text-gray-400 uppercase cursor-default select-none">
                Admin Menu
              </h3>
              <div className="h-64 overflow-hidden">
                <FadeOut className="h-64 pt-3 -ml-2 overflow-y-scroll white-scrollbar">
                  {adminMenuItems.map(({ to, label, icon }, i) => (
                    <MenuItem
                      tight
                      key={i}
                      to={to}
                      label={label}
                      Icon={icon}
                      textClass="text-gray-400"
                      activeTextClass="text-white"
                      activeIndicatorClass="bg-gray-800"
                    />
                  ))}
                </FadeOut>
              </div>
            </div>
          )}
          {process.env.REACT_APP_VERSION && (
            <div className="mb-2 mr-4 text-xs text-right text-white opacity-50">
              V{process.env.REACT_APP_VERSION}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default AppMenu;

const FadeOut = styled.ul`
  mask-image: linear-gradient(
    to bottom,
    transparent,
    black 20px,
    black 90%,
    transparent
  );
`;
